import { Component, input, ChangeDetectionStrategy, computed, signal, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, AbstractControl, FormArray, ValidationErrors } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

export interface ValidationError {
  field: string;
  fieldLabel: string;
  message: string;
  control: AbstractControl | null;
}

@Component({
  selector: 'app-validation-summary',
  imports: [CommonModule, TranslateModule],
  templateUrl: './validation-summary.component.html',
  styleUrls: ['./validation-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ValidationSummaryComponent implements OnDestroy {
  form = input.required<FormGroup>();
  title = input<string>('Check fields');
  show = input<boolean>(false);
  additionalErrors = input<ValidationError[]>([]);

  private readonly _refreshTrigger = signal(0);
  private readonly _destroy$ = new Subject<void>();

  constructor() {
    effect(() => {
      const form = this.form();
      this._destroy$.next();
      form.statusChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this._refreshTrigger.update(v => v + 1);
      });
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  protected errors = computed(() => {
    this._refreshTrigger();
    if (!this.show() || !this.form()) {
      return [];
    }

    const errors: ValidationError[] = [];
    this.collectErrors(this.form(), '', errors);
    const combined = [...errors, ...this.additionalErrors()];
    return combined.filter((error, index) => combined.findIndex(e => e.fieldLabel === error.fieldLabel) === index);
  });

  protected get hasErrors(): boolean {
    return this.errors().length > 0;
  }

  private collectErrors(
    control: AbstractControl,
    path: string,
    errors: ValidationError[]
  ): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      if (control.invalid && control.errors) {
        const fieldLabel = path === '' ? this.getRootErrorLabel(control.errors) : this.getFieldLabel(path);
        if (fieldLabel != undefined) {
          errors.push({ field: path, fieldLabel, message: '', control });
        }
      }

      Object.keys(control.controls).forEach(key => {
        const childControl = control.get(key);
        const childPath = path ? `${path}.${key}` : key;
        
        if (childControl) {
          this.collectErrors(childControl, childPath, errors);
        }
      });
    }
    else if (control.invalid) {
      const fieldLabel = this.getFieldLabel(path);

      if (fieldLabel == undefined) return;
      
      errors.push({
        field: path,
        fieldLabel,
        message: '',
        control
      });
    }
  }

  private getRootErrorLabel(errors: ValidationErrors): string | undefined {
    if (errors['publicationStartAfterTimeSpanStart']) return 'Publication time';
    return undefined;
  }

  private getFieldLabel(path: string): string | undefined {
    if (['hastusRegion', 'garageCode', 'replacePlaceId', 'routeName'].some(key => path === key || path.endsWith(`.${key}`))) return;

    const customIndex = path.indexOf('.custom.');
    if (customIndex !== -1) {
      return path.slice(customIndex + 10, path.length);
    }

    const labelMap: Record<string, string> = {
      'deviationReason': 'Cause',
      'affected': 'Time span',
      'affected.startDate': 'Time span - Start time',
      'affected.startTime': 'Time span - Start time',
      'affected.endDate': 'Time span - End time',
      'affected.endTime': 'Time span - End time',
      'affected.routeInfo': 'Affected traffic',
      'internalComment': 'Internal comment',
      'trafficDeviationInformation.summary': 'Summary',
      'trafficDeviationInformation.header': 'Header',
      'trafficDeviationInformation.content': 'Content',
      'trafficDeviationInformation.allChipsHasValue': 'All chips in the template must be populated',
      'trafficDeviationInformation.publicationStartDate': 'Publication time',
      'trafficDeviationInformation.publicationStartTime': 'Publication time',
      'trafficDeviationInformation.publicationEndDate': 'Publication time',
      'trafficDeviationInformation.publicationEndTime': 'Publication time',
      'trafficDeviationInformation.displayScope': 'Channels'
    };

    return labelMap[path] || this.formatFieldName(path);
  }

  private formatFieldName(path: string): string | undefined {
    const parts = path.split('.');
    const lastPart = parts.at(-1) || '';

    if (!lastPart) return undefined;

    return lastPart
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}
